// The curriculum's headless model: the canonical mastery/unlock logic and the
// learner's proficiency. Pure leaf — consumed by the sequencer (pick next) and
// the discovery-map UI (Spec B). One copy of `satisfied()` lives here (two copies
// caused the Phase-2 reachability bug). No engine/render/RNG.
import type { SkillId } from "../types";
import type { CatalogEntry } from "./catalog";
import { difficulty } from "./difficulty";
import { SKILLS } from "./skills";

const REACH = 0.25; // difficulty window above proficiency (a quarter-grade stretch)
const TOP_K = 5; // proficiency = average of the K hardest-mastered items
const FLOOR = 0.5; // proficiency with nothing mastered yet (Preparatory, the corpus floor)

const SKILL_BY_ID = new Map(SKILLS.map((s) => [s.id, s]));

interface Mastery {
  masteredAtoms: ReadonlySet<SkillId>;
  /** Mastered, or an undrilled conceptual atom satisfied via its prereqs. */
  satisfied(id: SkillId): boolean;
}

/** The atoms a learner has, and the canonical transparency rule over them. */
export function buildMastery(
  catalog: readonly CatalogEntry[],
  masteredIds: ReadonlySet<string>,
): Mastery {
  const drills = catalog.filter((e) => (e.teaches?.length ?? 0) > 0);
  const masteredAtoms = new Set<SkillId>();
  for (const d of drills) {
    if (masteredIds.has(d.id)) for (const s of d.teaches ?? []) masteredAtoms.add(s);
  }
  const hasDrill = (id: SkillId): boolean => drills.some((d) => (d.teaches ?? []).includes(id));
  const satisfied = (id: SkillId): boolean => {
    if (masteredAtoms.has(id)) return true;
    if (hasDrill(id)) return false;
    const sk = SKILL_BY_ID.get(id);
    return sk ? sk.prereqs.every(satisfied) : true;
  };
  return { masteredAtoms, satisfied };
}

/** Top-K ceiling: average difficulty of the learner's K hardest-mastered items. */
export function proficiency(
  catalog: readonly CatalogEntry[],
  masteredIds: ReadonlySet<string>,
): number {
  const scores = catalog
    .filter((e) => masteredIds.has(e.id))
    .map(difficulty)
    .sort((a, b) => b - a);
  if (scores.length === 0) return FLOOR;
  const top = scores.slice(0, TOP_K);
  return top.reduce((s, x) => s + x, 0) / top.length;
}

type LockReason = null | { kind: "atom"; missing: SkillId[] } | { kind: "difficulty"; gap: number };

export interface SongState {
  entry: CatalogEntry;
  bundle: SkillId[];
  difficulty: number;
  unlocked: boolean;
  lockReason: LockReason;
}

export interface CurriculumState {
  proficiency: number;
  songs: SongState[];
}

/** Classify every not-yet-mastered apply piece by the two-gate unlock rule. */
export function curriculumState(
  catalog: readonly CatalogEntry[],
  masteredIds: ReadonlySet<string>,
): CurriculumState {
  const { satisfied } = buildMastery(catalog, masteredIds);
  const prof = proficiency(catalog, masteredIds);
  const window = prof + REACH;
  const songs = catalog
    .filter((e) => (e.uses?.length ?? 0) > 0 && !masteredIds.has(e.id))
    .map<SongState>((entry) => {
      const bundle = entry.uses ?? [];
      // The unlock gate is `uses − teaches`: a song isn't required to already know the skill
      // it itself introduces (its placement delta). Only the all-but-the-delta prereqs gate it.
      const teaches = new Set(entry.teaches ?? []);
      const prereqs = bundle.filter((a) => !teaches.has(a));
      const missing = prereqs.filter((a) => !satisfied(a));
      const diff = difficulty(entry);
      const atomOk = missing.length === 0;
      const diffOk = diff <= window;
      const lockReason: LockReason = !atomOk
        ? { kind: "atom", missing }
        : !diffOk
          ? { kind: "difficulty", gap: Math.round((diff - window) * 4) / 4 }
          : null;
      return { entry, bundle, difficulty: diff, unlocked: atomOk && diffOk, lockReason };
    });
  return { proficiency: prof, songs };
}
